import { createContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { UserProfile } from '@/types/userProfile';
import { isAdminRole } from '@/types/userProfile';
import type { Employee } from '@/types/employee';

export interface CustomerProfile {
  id: string;
  full_name: string;
  phone: string | null;
  photo_url: string | null;
  marketing_consent: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  employee: Employee | null;
  customerProfile: CustomerProfile | null;
  /** True si l'user est un client (a customer_profiles, pas user_profiles) */
  isCustomer: boolean;
  isAdminOrOwner: boolean;
  /** Set des permissions accordées à l'utilisateur connecté. */
  permissions: Set<string>;
  /** Helper : vérifie une permission exacte (ex: 'shifts.kpi'). */
  hasPermission: (id: string) => boolean;
  /** Helper : vérifie si l'utilisateur a au moins une perm de la section. */
  hasSectionAccess: (sectionId: string) => boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  /**
   * Charge le profile (user_profiles), l'employé associé et les
   * permissions (user_section_access) à partir d'un user Supabase.
   *
   * Toléré aux pannes : try/catch global évite que loading reste figé.
   */
  const loadProfile = useCallback(async (u: User | null) => {
    if (!u) {
      setProfile(null);
      setEmployee(null);
      setCustomerProfile(null);
      setPermissions(new Set());
      return;
    }

    try {
      // Profile + permissions + customer_profile en parallèle (3 reqs indépendantes)
      const [profileRes, permsRes, customerRes] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('*')
          .eq('id', u.id)
          .maybeSingle(),
        supabase
          .from('user_section_access')
          .select('section_id')
          .eq('user_id', u.id),
        supabase
          .from('customer_profiles')
          .select('*')
          .eq('id', u.id)
          .maybeSingle(),
      ]);

      if (profileRes.error) {
        if (import.meta.env.DEV) {
          console.warn(
            'Erreur chargement user_profile:',
            profileRes.error.message
          );
        }
        setProfile(null);
        setEmployee(null);
      } else {
        const p = (profileRes.data as UserProfile | null) ?? null;
        setProfile(p);

        if (p?.employee_id) {
          const { data: empData } = await supabase
            .from('employees')
            .select('*')
            .eq('id', p.employee_id)
            .maybeSingle();
          setEmployee((empData as Employee | null) ?? null);
        } else {
          setEmployee(null);
        }
      }

      if (permsRes.error) {
        if (import.meta.env.DEV) {
          console.warn(
            'Erreur chargement user_section_access:',
            permsRes.error.message
          );
        }
        setPermissions(new Set());
      } else {
        setPermissions(
          new Set((permsRes.data ?? []).map((r) => r.section_id as string))
        );
      }

      // customer_profile : ignore l'erreur si la table n'existe pas encore
      // (migration 20260510_004 pas appliquée).
      if (customerRes.error) {
        if (import.meta.env.DEV) {
          console.warn(
            'Erreur chargement customer_profiles:',
            customerRes.error.message
          );
        }
        setCustomerProfile(null);
      } else {
        let existing = (customerRes.data as CustomerProfile | null) ?? null;

        // Auto-création (self-healing) du customer_profile pour TOUT user
        // connecté qui n'est pas staff et n'a pas encore de fiche client.
        //
        // Couvre deux cas :
        //   • signup OAuth (Google, Apple…) → provider != 'email'
        //   • signup courriel/mot-de-passe dont le trigger DB
        //     `handle_new_customer_user` n'a pas créé la fiche (inscription
        //     avant le trigger, période « Confirm email », échec silencieux
        //     avalé par le EXCEPTION du trigger).
        // Sans ce filet, ces clients restent orphelins : invisibles côté
        // admin ET bloqués sur la roulette infinie de /mon-compte.
        //
        // Garde anti-staff : on ne crée RIEN si l'user a un user_profile
        // (= employé). La RLS `customer_profiles_insert_self` autorise
        // l'INSERT de sa propre fiche (id = auth.uid()).
        const isNewClient =
          !existing && !(profileRes.data as UserProfile | null);

        if (isNewClient) {
          const meta = u.user_metadata ?? {};
          const fullName =
            (meta.full_name as string | undefined) ||
            (meta.name as string | undefined) ||
            [meta.given_name, meta.family_name].filter(Boolean).join(' ') ||
            u.email?.split('@')[0] ||
            'Client';

          // Photo : Google fournit `picture`, parfois `avatar_url`.
          // On accepte uniquement les URL http(s) pour éviter d'insérer
          // une string vide ou un placeholder.
          const rawPhoto =
            (meta.picture as string | undefined) ||
            (meta.avatar_url as string | undefined);
          const photoUrl =
            rawPhoto && /^https?:\/\//i.test(rawPhoto) ? rawPhoto : null;

          // Marketing : respecte le choix réel du client (mis dans
          // options.data au signup courriel) ; défaut true si absent.
          const marketingConsent =
            typeof meta.marketing_consent === 'boolean'
              ? (meta.marketing_consent as boolean)
              : true;

          const { data: created, error: createErr } = await supabase
            .from('customer_profiles')
            .insert({
              id: u.id,
              full_name: fullName,
              phone: (meta.phone as string | null | undefined) ?? null,
              marketing_consent: marketingConsent,
              photo_url: photoUrl,
            })
            .select()
            .maybeSingle();

          if (createErr) {
            if (import.meta.env.DEV) {
              console.warn(
                'Auto-création customer_profile échouée:',
                createErr.message
              );
            }
          } else {
            existing = (created as CustomerProfile | null) ?? null;
          }
        }

        setCustomerProfile(existing);
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn(
          'Exception inattendue dans loadProfile :',
          err instanceof Error ? err.message : err
        );
      }
      setProfile(null);
      setEmployee(null);
      setCustomerProfile(null);
      setPermissions(new Set());
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let resolved = false;

    const finishLoading = () => {
      if (!mounted || resolved) return;
      resolved = true;
      setLoading(false);
    };

    // Filet de sécurité court : si même getSession() hang, on débloque
    // après 2 secondes. L'app s'affichera (en mode non connecté si la
    // session n'a pas pu être lue) — pas de "Chargement…" infini.
    const safetyTimeout = setTimeout(() => {
      if (!resolved && import.meta.env.DEV) {
        console.warn('Auth safety timeout (2s) — débloque le loading');
      }
      finishLoading();
    }, 2000);

    // 1) Lire la session locale ASAP. On débloque le loading DÈS qu'on
    //    a la session — sans attendre le profile (qui se charge en
    //    arrière-plan). Plus de F5 lent à cause d'une RLS profile.
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        finishLoading(); // ← débloque l'UI immédiatement
        // Profile en background — l'UI se met à jour quand ça arrive
        if (session?.user) {
          loadProfile(session.user);
        }
      })
      .catch((err) => {
        if (import.meta.env.DEV) {
          console.warn(
            'getSession() error :',
            err instanceof Error ? err.message : err
          );
        }
        finishLoading();
      });

    // 2) Listener pour les changements (login, logout, refresh token).
    //    Recharge le profile en background à chaque event, mais ne
    //    bloque jamais l'UI.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      finishLoading();

      // Cleanup explicite en cas de signout / suppression user
      if (event === 'SIGNED_OUT') {
        setProfile(null);
        setEmployee(null);
        setCustomerProfile(null);
        return;
      }

      // Recharge profile en background (peut prendre du temps,
      // pas grave, l'UI est déjà débloquée)
      if (session?.user) {
        loadProfile(session.user);
      } else {
        setProfile(null);
        setEmployee(null);
        setCustomerProfile(null);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshProfile = useCallback(async () => {
    await loadProfile(user);
  }, [loadProfile, user]);

  const isAdminOrOwner = isAdminRole(profile?.role);
  // Règle stricte (per design : staff XOR client) :
  //   si l'user a un customer_profile → traité comme client, PEU IMPORTE
  //   l'existence d'un user_profile résiduel. Un compte créé via
  //   /compte/inscription ne doit JAMAIS voir l'app staff. Si un user
  //   est en état hybride (a les 2), on privilégie le rôle le plus
  //   restrictif (client) — l'admin peut nettoyer manuellement.
  const isCustomer = !!customerProfile;

  const hasPermission = useCallback(
    (id: string) => permissions.has(id),
    [permissions]
  );

  const hasSectionAccess = useCallback(
    (sectionId: string) => {
      const prefix = sectionId + '.';
      for (const p of permissions) {
        if (p.startsWith(prefix)) return true;
      }
      return false;
    },
    [permissions]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        employee,
        customerProfile,
        isCustomer,
        isAdminOrOwner,
        permissions,
        hasPermission,
        hasSectionAccess,
        loading,
        signIn,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
